import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Alert, Button, Form, Card, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { recipesAPI } from '../utils/api';
import OrderForm from '../components/OrderForm';

const RecipeEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [recipeName, setRecipeName] = useState('');
  const [recipeDescription, setRecipeDescription] = useState('');
  const [initialMaterials, setInitialMaterials] = useState([]);
  const [initialDiscountVat, setInitialDiscountVat] = useState(0);
  const [initialDiscountNonVat, setInitialDiscountNonVat] = useState(0);
  const [initialMarkupPercent, setInitialMarkupPercent] = useState(0);

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        const res = await recipesAPI.getById(id);
        const recipe = res.data.data;

        setRecipeName(recipe.name);
        setRecipeDescription(recipe.description || '');
        setInitialDiscountVat(recipe.discountVat || 0);
        setInitialDiscountNonVat(recipe.discountNonVat || 0);
        setInitialMarkupPercent(recipe.markupPercent || 0);

        const materials = recipe.items.map(item => ({
          materialId: item.materialId || item.material?.id,
          materialCode: item.material?.code || '',
          materialName: item.material?.name || '',
          unit: item.material?.unit || '',
          quantity: item.quantity,
          unitCost: item.unitCost || item.material?.salePrice || 0,
          note: item.note || '',
          hasVat: item.hasVat || false
        }));

        setInitialMaterials(materials);
      } catch (err) {
        setError('ไม่พบสูตรที่ต้องการแก้ไข หรือเกิดข้อผิดพลาดในการดึงข้อมูล');
      } finally {
        setFetching(false);
      }
    };

    fetchRecipe();
  }, [id]);

  const handleSubmit = async (formData) => {
    setError('');

    if (!recipeName.trim()) {
      setError('กรุณาระบุชื่อสูตร');
      return;
    }

    if (formData.selectedMaterials.length === 0) {
      setError('กรุณาเพิ่มวัตถุดิบอย่างน้อย 1 รายการ');
      return;
    }

    setLoading(true);

    try {
      const recipeData = {
        name: recipeName.trim(),
        description: recipeDescription.trim() || undefined,
        discountVat: formData.discountVat || 0,
        discountNonVat: formData.discountNonVat || 0,
        markupPercent: formData.markupPercent || 0,
        items: formData.selectedMaterials.map(item => ({
          materialId: item.materialId,
          quantity: item.quantity,
          unitCost: item.unitCost,
          note: item.note,
          hasVat: item.hasVat || false
        }))
      };

      await recipesAPI.update(id, recipeData);
      alert('อัปเดตสูตรสำเร็จ');
      navigate(`/recipes/${id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการอัปเดตสูตร');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Container fluid className="main-content">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      </Container>
    );
  }

  if (error && initialMaterials.length === 0) {
    return (
      <Container fluid className="main-content">
        <Alert variant="danger">{error}</Alert>
        <Button variant="outline-secondary" onClick={() => navigate(`/recipes/${id}`)}>
          <i className="bi bi-arrow-left me-2"></i>กลับ
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <Button
            variant="outline-secondary"
            size="sm"
            className="mb-2"
            onClick={() => navigate(`/recipes/${id}`)}
          >
            <i className="bi bi-arrow-left me-1"></i>
            กลับ
          </Button>
          <h2>
            <i className="bi bi-journal-pen me-2"></i>
            แก้ไขสูตร
          </h2>
          <p className="text-muted">แก้ไขรายการวัตถุดิบและปริมาณของสูตรนี้</p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Body>
              <Card.Title>
                <i className="bi bi-card-text me-2"></i>
                ข้อมูลสูตร
              </Card.Title>
              <Form.Group className="mb-3">
                <Form.Label>ชื่อสูตร <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="ระบุชื่อสูตร"
                  value={recipeName}
                  onChange={(e) => setRecipeName(e.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>รายละเอียดเพิ่มเติม</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="หมายเหตุหรือรายละเอียดเพิ่มเติม (ไม่บังคับ)"
                  value={recipeDescription}
                  onChange={(e) => setRecipeDescription(e.target.value)}
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <OrderForm
            initialSelectedMaterials={initialMaterials}
            initialDiscountVat={initialDiscountVat}
            initialDiscountNonVat={initialDiscountNonVat}
            initialMarkupPercent={initialMarkupPercent}
            showMarkupInput={true}
            onSubmit={handleSubmit}
            loading={loading}
            submitButtonText="บันทึกการแก้ไข"
            submitButtonIcon="bi-floppy"
            allowPriceEdit={false}
          />

          <div className="d-grid gap-2 mb-4">
            <Button
              type="button"
              variant="outline-secondary"
              onClick={() => navigate(`/recipes/${id}`)}
            >
              <i className="bi bi-x-circle me-2"></i>
              ยกเลิก
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default RecipeEdit;
